import { useEffect, useState, useRef } from "react";
import {
  Mail,
  Send,
  Save,
  Trash2,
  Eye,
  Plus,
  FileText,
  Users,
  Calendar,
  UserCheck,
  MailPlus,
  X,
  CheckCircle,
  XCircle,
  Clock,
} from "lucide-react";
import {
  getAllCampaigns,
  getAllTemplates,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  sendCampaign,
  getCampaignLogs,
  getCampaignStats,
  getRecipientCount,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from "../services/email.service";
import toast from "react-hot-toast";

export default function MailSystem() {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState({
    total_campaigns: 0,
    draft_campaigns: 0,
    sent_campaigns: 0,
    total_emails_sent: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [campaignLogs, setCampaignLogs] = useState([]);
  const [filter, setFilter] = useState("all"); // all, draft, sent
  const [activeTab, setActiveTab] = useState("campaigns"); // campaigns, templates
  
  // Form state
  const [form, setForm] = useState({
    subject: "",
    body: "",
    recipient_type: "custom",
    custom_recipients: "",
    template_id: null,
  });

  // Template form state
  const [templateForm, setTemplateForm] = useState({
    name: "",
    subject: "",
    body: "",
    category: "",
    is_active: true,
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  const editorRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (form.recipient_type !== "custom") {
      loadRecipientCount();
    }
  }, [form.recipient_type]);

  async function loadData() {
    setLoading(true);
    const [campaignsData, templatesData, statsData] = await Promise.all([
      getAllCampaigns(),
      getAllTemplates(),
      getCampaignStats(),
    ]);
    setCampaigns(campaignsData);
    setTemplates(templatesData);
    setStats(statsData);
    setLoading(false);
  }

  async function loadRecipientCount() {
    const count = await getRecipientCount(form.recipient_type);
    setRecipientCount(count);
  }

  const handleSaveDraft = async () => {
    if (!form.subject.trim() || !form.body.trim()) {
      toast.error("Subject and body are required");
      return;
    }

    setIsSubmitting(true);

    const customRecipientsArray =
      form.recipient_type === "custom"
        ? form.custom_recipients
            .split(",")
            .map((email) => email.trim())
            .filter((email) => email)
        : null;

    const campaignData = {
      subject: form.subject,
      body: form.body,
      recipient_type: form.recipient_type,
      custom_recipients: customRecipientsArray,
      template_id: form.template_id,
      status: "draft",
    };

    const result = selectedCampaign
      ? await updateCampaign(selectedCampaign.id, campaignData)
      : await createCampaign(campaignData);

    if (result.success) {
      toast.success(result.message);
      resetForm();
      loadData();
    } else {
      toast.error(result.message);
    }

    setIsSubmitting(false);
  };

  const handleSendCampaign = async (campaign) => {
    if (
      !window.confirm(
        `Are you sure you want to send this campaign to ${
          campaign.total_recipients || "all"
        } recipients?`
      )
    ) {
      return;
    }

    setIsSubmitting(true);
    const result = await sendCampaign(campaign.id);

    if (result.success) {
      toast.success(result.message);
      loadData();
    } else {
      toast.error(result.message);
    }

    setIsSubmitting(false);
  };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm("Are you sure you want to delete this campaign?")) {
      return;
    }

    const result = await deleteCampaign(id);

    if (result.success) {
      toast.success(result.message);
      loadData();
    } else {
      toast.error(result.message);
    }
  };

  const handleTemplateSelect = (template) => {
    setForm({
      ...form,
      subject: template.subject,
      body: template.body,
      template_id: template.id,
    });
    toast.success("Template loaded!");
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setForm({
      subject: campaign.subject,
      body: campaign.body,
      recipient_type: campaign.recipient_type,
      custom_recipients: campaign.custom_recipients?.join(", ") || "",
      template_id: campaign.template_id,
    });
    setShowComposer(true);
  };

  const handleViewLogs = async (campaign) => {
    setSelectedCampaign(campaign);
    const logs = await getCampaignLogs(campaign.id);
    setCampaignLogs(logs);
    setShowLogs(true);
  };

  const resetForm = () => {
    setForm({
      subject: "",
      body: "",
      recipient_type: "custom",
      custom_recipients: "",
      template_id: null,
    });
    setSelectedCampaign(null);
    setShowComposer(false);
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: "",
      subject: "",
      body: "",
      category: "",
      is_active: true,
    });
    setSelectedTemplate(null);
    setShowTemplateForm(false);
  };

  const handleCreateTemplate = () => {
    resetTemplateForm();
    setShowTemplateForm(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body: template.body,
      category: template.category,
      is_active: template.is_active,
    });
    setShowTemplateForm(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.subject.trim() || !templateForm.body.trim()) {
      toast.error("Name, subject, and body are required");
      return;
    }

    setIsSubmitting(true);

    const result = selectedTemplate
      ? await updateTemplate(selectedTemplate.id, templateForm)
      : await createTemplate(templateForm);

    if (result.success) {
      toast.success(result.message);
      resetTemplateForm();
      loadData();
    } else {
      toast.error(result.message);
    }

    setIsSubmitting(false);
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }

    const result = await deleteTemplate(id);

    if (result.success) {
      toast.success(result.message);
      loadData();
    } else {
      toast.error(result.message);
    }
  };

  const getFilteredCampaigns = () => {
    if (filter === "draft") return campaigns.filter((c) => c.status === "draft");
    if (filter === "sent") return campaigns.filter((c) => c.status === "sent");
    return campaigns;
  };

  const filteredCampaigns = getFilteredCampaigns();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-5 flex items-start gap-4">
        <Mail className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-lg font-bold text-slate-900 mb-2">
            Email Campaign Management
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            Create and send email campaigns to your subscribers, conference participants, or club members. 
            Use templates for quick composition, save drafts, and track delivery status.
            <span className="font-semibold"> Powered by Resend (3,000 emails/month free).</span>
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
        <StatCard
          title="Total Campaigns"
          value={stats.total_campaigns}
          icon={<Mail className="w-5 h-5" />}
        />
        <StatCard
          title="Draft Campaigns"
          value={stats.draft_campaigns}
          icon={<FileText className="w-5 h-5" />}
          color="amber"
        />
        <StatCard
          title="Sent Campaigns"
          value={stats.sent_campaigns}
          icon={<Send className="w-5 h-5" />}
          color="green"
        />
        <StatCard
          title="Total Emails Sent"
          value={stats.total_emails_sent}
          icon={<CheckCircle className="w-5 h-5" />}
          color="blue"
        />
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm p-1 flex gap-1">
        <button
          onClick={() => setActiveTab("campaigns")}
          className={`flex-1 px-4 py-3 rounded-md font-semibold text-sm transition-all ${
            activeTab === "campaigns"
              ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Mail size={18} />
            Email Campaigns
          </div>
        </button>
        <button
          onClick={() => setActiveTab("templates")}
          className={`flex-1 px-4 py-3 rounded-md font-semibold text-sm transition-all ${
            activeTab === "templates"
              ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <FileText size={18} />
            Email Templates ({templates.length})
          </div>
        </button>
      </div>

      {/* Actions Bar */}
      {activeTab === "campaigns" && (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 rounded-lg shadow-sm">
          <div className="flex gap-2">
            <FilterButton
              active={filter === "all"}
              onClick={() => setFilter("all")}
              label="All"
            />
            <FilterButton
              active={filter === "draft"}
              onClick={() => setFilter("draft")}
              label="Drafts"
            />
            <FilterButton
              active={filter === "sent"}
              onClick={() => setFilter("sent")}
              label="Sent"
            />
          </div>

          <button
            onClick={() => {
              resetForm();
              setShowComposer(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all text-sm font-bold shadow-lg"
          >
            <MailPlus size={18} />
            New Campaign
          </button>
        </div>
      )}

      {activeTab === "templates" && (
        <div className="flex justify-end bg-white p-4 rounded-lg shadow-sm">
          <button
            onClick={handleCreateTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all text-sm font-bold shadow-lg"
          >
            <Plus size={18} />
            New Template
          </button>
        </div>
      )}

      {/* Campaigns List */}
      {activeTab === "campaigns" && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">
                    Recipient Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">
                    Recipients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredCampaigns.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                      No campaigns found. Create your first campaign!
                    </td>
                  </tr>
                ) : (
                  filteredCampaigns.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">{campaign.subject}</div>
                      </td>
                      <td className="px-6 py-4">
                        <RecipientTypeBadge type={campaign.recipient_type} />
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={campaign.status} />
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {campaign.sent_count || 0} / {campaign.total_recipients || 0}
                        {campaign.failed_count > 0 && (
                          <span className="text-red-600 ml-2">
                            ({campaign.failed_count} failed)
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(campaign.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {campaign.status === "draft" && (
                            <>
                              <button
                                onClick={() => handleEditCampaign(campaign)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <FileText size={16} />
                              </button>
                              <button
                                onClick={() => handleSendCampaign(campaign)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Send"
                                disabled={isSubmitting}
                              >
                                <Send size={16} />
                              </button>
                            </>
                          )}
                          {campaign.status === "sent" && (
                            <button
                              onClick={() => handleViewLogs(campaign)}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="View Logs"
                            >
                              <Eye size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteCampaign(campaign.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Templates List */}
      {activeTab === "templates" && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">
                    Template Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {templates.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                      No templates found. Create your first template!
                    </td>
                  </tr>
                ) : (
                  templates.map((template) => (
                    <tr key={template.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900">{template.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                          {template.category || "General"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 max-w-md truncate">
                        {template.subject}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            template.is_active
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {template.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(template.updated_at || template.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditTemplate(template)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <FileText size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Email Composer Modal */}
      {showComposer && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">
                {selectedCampaign ? "Edit Campaign" : "New Email Campaign"}
              </h3>
              <button
                onClick={resetForm}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Template Selector */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Load from Template (Optional)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className="p-3 border border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                    >
                      <div className="font-medium text-sm text-slate-900">
                        {template.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {template.category}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Recipient Type */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Send To
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <RecipientOption
                    active={form.recipient_type === "custom"}
                    onClick={() => setForm({ ...form, recipient_type: "custom" })}
                    icon={<Users size={18} />}
                    label="Custom"
                  />
                  <RecipientOption
                    active={form.recipient_type === "newsletter"}
                    onClick={() => setForm({ ...form, recipient_type: "newsletter" })}
                    icon={<Mail size={18} />}
                    label="Newsletter"
                  />
                  <RecipientOption
                    active={form.recipient_type === "conference"}
                    onClick={() => setForm({ ...form, recipient_type: "conference" })}
                    icon={<Calendar size={18} />}
                    label="Conference"
                  />
                  <RecipientOption
                    active={form.recipient_type === "club"}
                    onClick={() => setForm({ ...form, recipient_type: "club" })}
                    icon={<UserCheck size={18} />}
                    label="Club"
                  />
                </div>
                <div className="text-xs text-slate-500 mt-2 space-y-1">
                  {form.recipient_type === "newsletter" && (
                    <p className="text-blue-600 font-medium">
                      📧 All active newsletter subscribers ({recipientCount} recipients)
                    </p>
                  )}
                  {form.recipient_type === "conference" && (
                    <p className="text-purple-600 font-medium">
                      🎫 All conference registered participants ({recipientCount} recipients)
                    </p>
                  )}
                  {form.recipient_type === "club" && (
                    <p className="text-green-600 font-medium">
                      👥 All AfricaMed club members ({recipientCount} recipients)
                    </p>
                  )}
                  {form.recipient_type === "custom" && (
                    <p className="text-amber-600 font-medium">
                      ✏️ Enter specific email addresses (comma-separated) below
                    </p>
                  )}
                </div>
              </div>

              {/* Custom Recipients */}
              {form.recipient_type === "custom" && (
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Email Addresses (comma-separated)
                  </label>
                  <textarea
                    value={form.custom_recipients}
                    onChange={(e) =>
                      setForm({ ...form, custom_recipients: e.target.value })
                    }
                    placeholder="email1@example.com, email2@example.com"
                    rows={3}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-900"
                  />
                </div>
              )}

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Enter email subject"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-900"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Email Body (HTML supported)
                </label>
                <textarea
                  ref={editorRef}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder="Enter email content (HTML tags supported)"
                  rows={12}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm text-slate-900"
                />
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button
                onClick={resetForm}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPreview(true);
                }}
                className="flex-1 px-4 py-2 border border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              >
                <Eye size={18} />
                Preview
              </button>
              <button
                onClick={handleSaveDraft}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                disabled={isSubmitting}
              >
                <Save size={18} />
                {isSubmitting ? "Saving..." : "Save Draft"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">Email Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="mb-4 pb-4 border-b">
                <div className="text-sm text-slate-600 mb-2">Subject:</div>
                <div className="text-lg font-semibold text-slate-900">{form.subject}</div>
              </div>
              <div
                className="prose max-w-none text-slate-900"
                dangerouslySetInnerHTML={{ __html: form.body }}
              />
            </div>
            <div className="p-6 border-t">
              <button
                onClick={() => setShowPreview(false)}
                className="w-full px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      {showLogs && selectedCampaign && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Campaign Logs</h3>
                <p className="text-sm text-slate-600 mt-1">
                  {selectedCampaign.subject}
                </p>
              </div>
              <button
                onClick={() => setShowLogs(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-2">
                {campaignLogs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {log.status === "sent" ? (
                        <CheckCircle size={18} className="text-green-600" />
                      ) : (
                        <XCircle size={18} className="text-red-600" />
                      )}
                      <span className="text-sm font-medium text-slate-900">{log.recipient_email}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`text-xs px-2 py-1 rounded-full font-semibold ${
                          log.status === "sent"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {log.status}
                      </span>
                      {log.sent_at && (
                        <span className="text-xs text-slate-900">
                          {new Date(log.sent_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 border-t">
              <button
                onClick={() => setShowLogs(false)}
                className="w-full px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Form Modal */}
      {showTemplateForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">
                {selectedTemplate ? "Edit Template" : "Create New Template"}
              </h3>
              <button
                onClick={resetTemplateForm}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) =>
                    setTemplateForm({ ...templateForm, name: e.target.value })
                  }
                  placeholder="e.g., Conference Welcome Email"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-900"
                />
                <p className="text-xs text-slate-500 mt-1">
                  A descriptive name to identify this template
                </p>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Category
                </label>
                <select
                  value={templateForm.category}
                  onChange={(e) =>
                    setTemplateForm({ ...templateForm, category: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-900"
                >
                  <option value="">Select a category</option>
                  <option value="Newsletter">Newsletter</option>
                  <option value="Conference">Conference</option>
                  <option value="Club">Club</option>
                  <option value="Announcement">Announcement</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Event">Event</option>
                  <option value="Welcome">Welcome</option>
                  <option value="Follow-up">Follow-up</option>
                  <option value="General">General</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Organize templates by type for easier management
                </p>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Email Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={templateForm.subject}
                  onChange={(e) =>
                    setTemplateForm({ ...templateForm, subject: e.target.value })
                  }
                  placeholder="Enter email subject line"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-900"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Email Body (HTML supported) <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={templateForm.body}
                  onChange={(e) =>
                    setTemplateForm({ ...templateForm, body: e.target.value })
                  }
                  placeholder="Enter email content (HTML tags supported)"
                  rows={15}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm text-slate-900"
                />
                <p className="text-xs text-slate-500 mt-1">
                  You can use HTML tags for formatting. Example: &lt;strong&gt;bold&lt;/strong&gt;, &lt;p&gt;paragraph&lt;/p&gt;
                </p>
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={templateForm.is_active}
                  onChange={(e) =>
                    setTemplateForm({ ...templateForm, is_active: e.target.checked })
                  }
                  className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-slate-900">
                  Active Template
                </label>
                <p className="text-xs text-slate-500">
                  (Only active templates are shown when creating campaigns)
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button
                onClick={resetTemplateForm}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                disabled={isSubmitting}
              >
                <Save size={18} />
                {isSubmitting ? "Saving..." : selectedTemplate ? "Update Template" : "Create Template"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Helper Components */
function StatCard({ title, value, icon, color = "slate" }) {
  const colorClasses = {
    slate: "text-slate-900",
    amber: "text-amber-600",
    green: "text-green-600",
    blue: "text-blue-600",
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-500">{title}</p>
        <div className="text-slate-400">{icon}</div>
      </div>
      <p className={`text-3xl font-bold ${colorClasses[color]}`}>{value ?? "—"}</p>
    </div>
  );
}

function FilterButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        active
          ? "bg-blue-600 text-white shadow"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
    >
      {label}
    </button>
  );
}

function RecipientTypeBadge({ type }) {
  const badges = {
    newsletter: { label: "Newsletter", color: "bg-purple-100 text-purple-700" },
    conference: { label: "Conference", color: "bg-blue-100 text-blue-700" },
    club: { label: "Club", color: "bg-green-100 text-green-700" },
    custom: { label: "Custom", color: "bg-amber-100 text-amber-700" },
  };

  const badge = badges[type] || badges.custom;

  return (
    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badge.color}`}>
      {badge.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const badges = {
    draft: { label: "Draft", color: "bg-amber-100 text-amber-700", icon: <Clock size={12} /> },
    sending: { label: "Sending", color: "bg-blue-100 text-blue-700", icon: <Send size={12} /> },
    sent: { label: "Sent", color: "bg-green-100 text-green-700", icon: <CheckCircle size={12} /> },
    failed: { label: "Failed", color: "bg-red-100 text-red-700", icon: <XCircle size={12} /> },
  };

  const badge = badges[status] || badges.draft;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${badge.color}`}>
      {badge.icon}
      {badge.label}
    </span>
  );
}

function RecipientOption({ active, onClick, icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
        active
          ? "border-blue-500 bg-blue-50 text-blue-700"
          : "border-slate-200 hover:border-slate-300 text-slate-600"
      }`}
    >
      {icon}
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}
